/* =============================================
   DESERTED — Theme JavaScript
   ============================================= */

'use strict';

/* ---- Utilities ---- */
const $ = (selector, context = document) => context.querySelector(selector);
const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

function formatMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const value = (parseInt(cents, 10) / 100).toFixed(2);
  const format = window.shopData ? window.shopData.moneyFormat : '${{amount}}';
  return format
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.floor(parseFloat(value)))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
}

function showToast(message, duration = 3000) {
  let toast = $('#toast-notification');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'toast-notification';
    toast.className = 'toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('is-visible');
  clearTimeout(toast._timeout);
  toast._timeout = setTimeout(() => {
    toast.classList.remove('is-visible');
  }, duration);
}

/* ---- Announcement Bar ---- */
function initAnnouncementBar() {
  const bar = $('#announcement-bar');
  const closeBtn = $('#announcement-close');
  if (!bar || !closeBtn) return;

  const dismissed = sessionStorage.getItem('announcement-dismissed');
  if (dismissed) {
    bar.remove();
    return;
  }

  closeBtn.addEventListener('click', () => {
    bar.style.transition = 'height 0.3s ease, opacity 0.3s ease';
    bar.style.height = bar.offsetHeight + 'px';
    requestAnimationFrame(() => {
      bar.style.height = '0';
      bar.style.opacity = '0';
      bar.style.overflow = 'hidden';
    });
    setTimeout(() => {
      bar.remove();
      document.documentElement.style.removeProperty('--announce-offset');
    }, 320);
    sessionStorage.setItem('announcement-dismissed', '1');
  });
}

/* ---- Navigation ---- */
function initNav() {
  const header = $('.site-header');
  if (!header) return;

  const isHomepage = document.body.dataset.template === 'index';

  function updateNav() {
    const scrollY = window.scrollY;
    const threshold = window.innerHeight * 0.1;

    if (scrollY > threshold) {
      header.classList.add('scrolled');
    } else {
      if (isHomepage) {
        header.classList.remove('scrolled');
      } else {
        header.classList.add('scrolled');
      }
    }
  }

  if (!isHomepage) {
    header.classList.add('scrolled');
  }

  window.addEventListener('scroll', updateNav, { passive: true });
  updateNav();
}

/* ---- Mobile Menu ---- */
function initMobileMenu() {
  const menu = $('#mobile-menu');
  const overlay = $('#mobile-overlay');
  const closeBtn = $('#mobile-menu-close');
  const hamburger = $('.header__hamburger');

  if (!menu) return;

  function openMenu() {
    menu.classList.add('is-open');
    overlay.classList.add('is-visible');
    menu.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
    if (closeBtn) closeBtn.focus();
  }

  function closeMenu() {
    menu.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    menu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
    if (hamburger) hamburger.focus();
  }

  if (hamburger) hamburger.addEventListener('click', openMenu);
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);
  if (overlay) overlay.addEventListener('click', closeMenu);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) {
      closeMenu();
    }
  });
}

/* ---- Cart Drawer ---- */
let cartDrawerOpen = false;

function initCartDrawer() {
  const drawer = $('#cart-drawer');
  const overlay = $('#cart-overlay');
  const closeBtn = $('#cart-drawer-close');
  const cartTriggers = $$('.cart-trigger, [data-cart-trigger]');

  if (!drawer) return;

  window.openCartDrawer = function () {
    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
    cartDrawerOpen = true;
    refreshCartDrawer();
  };

  window.closeCartDrawer = function () {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open');
    cartDrawerOpen = false;
  };

  cartTriggers.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    window.openCartDrawer();
  }));

  if (closeBtn) closeBtn.addEventListener('click', window.closeCartDrawer);
  if (overlay) overlay.addEventListener('click', window.closeCartDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawerOpen) {
      window.closeCartDrawer();
    }
  });
}

function refreshCartDrawer() {
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
    })
    .catch(err => console.error('Cart fetch error:', err));
}

function renderCartDrawer(cart) {
  const body = $('#cart-drawer-body');
  const footer = $('#cart-drawer-footer');
  const subtotalEl = $('#cart-subtotal');

  if (!body) return;

  if (cart.item_count === 0) {
    body.innerHTML = `
      <div class="cart-drawer__empty">
        <p>YOUR CART IS EMPTY</p>
        <a href="/collections/all" class="btn btn--outline">CONTINUE SHOPPING</a>
      </div>
    `;
    if (footer) footer.style.display = 'none';
    return;
  }

  if (footer) footer.style.display = 'block';
  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

  const itemsHtml = cart.items.map(item => `
    <div class="cart-item" data-key="${item.key}">
      <div class="cart-item__image">
        ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''}
      </div>
      <div class="cart-item__details">
        <div class="cart-item__title">${item.product_title}</div>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
        <div class="cart-item__price">${formatMoney(item.line_price)}</div>
        <div class="cart-item__qty">
          <button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">
            &minus;
          </button>
          <span class="cart-item__qty-num">${item.quantity}</span>
          <button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">
            +
          </button>
        </div>
        <button class="cart-item__remove" data-key="${item.key}">Remove</button>
      </div>
    </div>
  `).join('');

  body.innerHTML = itemsHtml;

  // Bind quantity and remove buttons
  $$('.cart-item__qty-btn', body).forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const currentQty = parseInt(btn.dataset.qty, 10);
      const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
      updateCartItem(key, newQty);
    });
  });

  $$('.cart-item__remove', body).forEach(btn => {
    btn.addEventListener('click', () => {
      updateCartItem(btn.dataset.key, 0);
    });
  });
}

function updateCartItem(key, quantity) {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates: { [key]: quantity } })
  })
    .then(r => r.json())
    .then(cart => {
      renderCartDrawer(cart);
      updateCartCount(cart.item_count);
    })
    .catch(err => console.error('Update error:', err));
}

function updateCartCount(count) {
  $$('[data-cart-count]').forEach(el => {
    el.textContent = count;
    el.dataset.count = count;
  });
}

/* ---- Add to Cart (AJAX) ---- */
function initAddToCart() {
  const forms = $$('[data-add-to-cart-form]');

  forms.forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const formData = new FormData(form);
      const body = {};
      formData.forEach((val, key) => { body[key] = val; });

      if (!body.id) {
        showToast('Please select a size');
        return;
      }

      if (btn) {
        btn.disabled = true;
        btn.textContent = 'ADDING...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: body.id, quantity: 1 })
      })
        .then(r => r.json())
        .then(item => {
          if (btn) {
            btn.disabled = false;
            btn.textContent = 'ADD TO CART';
          }
          if (item.status && item.status !== 200) {
            showToast(item.description || 'Could not add to cart');
            return;
          }
          updateCartCount(0); // will refresh on drawer open
          fetch('/cart.js')
            .then(r => r.json())
            .then(cart => updateCartCount(cart.item_count));
          window.openCartDrawer && window.openCartDrawer();
        })
        .catch(err => {
          console.error('Add to cart error:', err);
          if (btn) {
            btn.disabled = false;
            btn.textContent = 'ADD TO CART';
          }
          showToast('Something went wrong');
        });
    });
  });
}

/* ---- Quick Add (product cards) ---- */
function initQuickAdd() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const variantId = btn.dataset.variantId;
    const productUrl = btn.dataset.productUrl;

    if (!variantId) {
      // Go to product page if no default variant
      if (productUrl) window.location.href = productUrl;
      return;
    }

    btn.textContent = 'ADDING...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(r => r.json())
      .then(() => {
        btn.textContent = 'ADDED!';
        setTimeout(() => { btn.textContent = 'QUICK ADD'; }, 1800);
        fetch('/cart.js').then(r => r.json()).then(cart => updateCartCount(cart.item_count));
        window.openCartDrawer && window.openCartDrawer();
      })
      .catch(() => {
        btn.textContent = 'QUICK ADD';
        showToast('Could not add to cart');
      });
  });
}

/* ---- Product Gallery (arrow navigation) ---- */
function initProductGallery() {
  const gallery = $('.product-gallery');
  if (!gallery) return;

  const slides = $$('.product-gallery__slide', gallery);
  const prevBtn = $('.product-gallery__arrow--prev', gallery);
  const nextBtn = $('.product-gallery__arrow--next', gallery);
  const counter = $('.product-gallery__counter', gallery);

  if (slides.length <= 1) {
    const controls = $('.product-gallery__controls', gallery);
    if (controls) controls.style.display = 'none';
    if (slides[0]) slides[0].classList.add('is-active');
    return;
  }

  let current = 0;

  function goTo(index) {
    slides[current].classList.remove('is-active');
    current = (index + slides.length) % slides.length;
    slides[current].classList.add('is-active');
    if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
  }

  goTo(0);

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

  // Swipe support
  let touchStartX = 0;
  gallery.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
  gallery.addEventListener('touchend', (e) => {
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goTo(current + 1) : goTo(current - 1);
    }
  });

  // Keyboard
  document.addEventListener('keydown', (e) => {
    if (!$('.product-gallery')) return;
    if (e.key === 'ArrowLeft') goTo(current - 1);
    if (e.key === 'ArrowRight') goTo(current + 1);
  });
}

/* ---- Product Card Hover Image Swap ---- */
function initCardHover() {
  // Handled entirely via CSS (opacity transition on .product-card:hover .img--secondary)
  // This function is a hook for any additional JS-based behavior if needed
  $$('.product-card').forEach(card => {
    const secondary = $('.product-card__img--secondary', card);
    if (!secondary) return;
    // CSS handles the crossfade — nothing extra needed
  });
}

/* ---- Size Selector ---- */
function initSizeSelector() {
  const sizeGrids = $$('.product__size-grid');

  sizeGrids.forEach(grid => {
    const form = grid.closest('form') || grid.closest('[data-add-to-cart-form]');
    const hiddenInput = form ? form.querySelector('input[name="id"]') : null;

    $$('.size-btn', grid).forEach(btn => {
      if (btn.dataset.unavailable) return;

      btn.addEventListener('click', () => {
        $$('.size-btn', grid).forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        if (hiddenInput && btn.dataset.variantId) {
          hiddenInput.value = btn.dataset.variantId;
        }

        // Update price if available
        const priceEl = form ? form.querySelector('.product__price') : null;
        if (priceEl && btn.dataset.price) {
          priceEl.textContent = formatMoney(btn.dataset.price);
        }

        // Update compare price
        const comparePriceEl = form ? form.querySelector('.product__price--compare') : null;
        if (comparePriceEl) {
          if (btn.dataset.comparePrice && parseInt(btn.dataset.comparePrice) > parseInt(btn.dataset.price)) {
            comparePriceEl.textContent = formatMoney(btn.dataset.comparePrice);
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }

        // Enable add to cart
        const atcBtn = form ? form.querySelector('[data-atc-btn]') : null;
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.textContent = 'ADD TO CART';
        }
      });
    });
  });
}

/* ---- Accordions ---- */
function initAccordions() {
  $$('.accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const accordion = trigger.closest('.accordion');
      const body = accordion.querySelector('.accordion__body');
      const isOpen = accordion.classList.contains('is-open');

      // Optionally close others in same group
      const group = accordion.closest('.product__accordions');
      if (group) {
        $$('.accordion.is-open', group).forEach(a => {
          if (a !== accordion) {
            a.classList.remove('is-open');
            a.querySelector('.accordion__trigger').setAttribute('aria-expanded', 'false');
          }
        });
      }

      accordion.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', !isOpen);
    });
  });
}

/* ---- Email Signup ---- */
function initEmailSignup() {
  $$('.email-signup__form').forEach(form => {
    form.addEventListener('submit', (e) => {
      const emailInput = form.querySelector('.email-signup__input');
      if (!emailInput || !emailInput.value) {
        e.preventDefault();
        emailInput && emailInput.focus();
        return;
      }
      // Let Shopify handle the form submission natively (form has correct action)
      // But show success state after submission on page load if ?customer_posted=true
    });
  });

  // Handle success state from URL param
  const url = new URL(window.location.href);
  if (url.searchParams.get('customer_posted') === 'true') {
    const success = $('.email-signup__success');
    const formEl = $('.email-signup__form');
    if (success) success.classList.add('is-visible');
    if (formEl) formEl.style.display = 'none';
  }
}

/* ---- Initial Cart Count Load ---- */
function initCartCount() {
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => updateCartCount(cart.item_count))
    .catch(() => {});
}

/* ---- Init ---- */
document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementBar();
  initNav();
  initMobileMenu();
  initCartDrawer();
  initAddToCart();
  initQuickAdd();
  initProductGallery();
  initCardHover();
  initSizeSelector();
  initAccordions();
  initEmailSignup();
  initCartCount();
});

/* ── Discount Popup ── */
(function() {
  const overlay = document.getElementById('popupOverlay');
  const closeBtn = document.getElementById('popupClose');
  const skipBtn  = document.getElementById('popupSkip');
  const form     = document.getElementById('popupForm');
  if (!overlay) return;
  if (sessionStorage.getItem('deserted-popup-seen')) return;

  setTimeout(function() { overlay.classList.add("visible"); }, 10000);

  function closePopup() {
    overlay.classList.remove('visible');
    sessionStorage.setItem('deserted-popup-seen', '1');
  }

  closeBtn && closeBtn.addEventListener('click', closePopup);
  skipBtn  && skipBtn.addEventListener('click',  closePopup);
  overlay.addEventListener('click', function(e) {
    if (e.target === overlay) closePopup();
  });

  if (form) {
    form.addEventListener('submit', function(e) {
      var btn = form.querySelector('button[type="submit"]'); sessionStorage.setItem('deserted-popup-seen', '1'); setTimeout(function() { btn.textContent = 'OFFER CLAIMED ✓'; btn.style.opacity = '0.6'; }, 400); setTimeout(closePopup, 1600);
    });
  }
})();
